const borderwidth = {
  name: "border-width",
  path: "/border-width",
  title: "border-width",
  description: "Utilities for controlling the width of an element's borders.",
  value: {
    'border-width': {
      'border': '1px',
      'border-<number>': '<number>px',
      'border-[<value>]': '<value>',
    },
    'border-inline-width': {
      'border-x': '1px',
      'border-x-<number>': '<number>px',
    },
    'border-block-width': {
      'border-y': '1px',
      'border-y-<number>': '<number>px',
    },
    'border-top-width': {
      'border-t': '1px',
      'border-t-<number>': '<number>px',
    },
    'border-right-width': {
      'border-r': '1px',
      'border-r-<number>': '<number>px',
    },
    'border-bottom-width': {
      'border-b': '1px',
      'border-b-<number>': '<number>px',
    },
    'border-left-width': {
      'border-l': '1px',
      'border-l-<number>': '<number>px',
    },
    '': {
      "divide-x":
        `& > :not(:last-child) {
border-right-width: 1px
}`,
      "divide-x-<number>":
        `& > :not(:last-child) {
border-right-width: <number>px
}`,
      "divide-y":
        `& > :not(:last-child) {
border-bottom-width: 1px
}`,
      "divide-y-<number>":
        `& > :not(:last-child) {
border-bottom-width: <number>px
}`,
    },
  },
  exampleData: [
    {
      example: {
        code: `<div class="border">border</div>
<div class= border-2">border-2</div>
<div class= border-4">border-4</div>
<div class= border-[10px]">border-[10px]</div>       `
      }
    },
    {
      example: {
        code: `<div class="border-t ...">border-t</div>
<div class="border-r-2 ...">border-r-2</div>
<div class="border-b-4 ...">border-b-4</div>
<div class="border-l-5 ...">border-l-5</div>
`
      }
    },
    {
      example: {
        code: `<div class="border-x-4 ...">border-x-4</div>
<div class="border-y-4 ...">border-y-4</div>
`
      }
    },
    {
      example: {
        code: `<div class="grid grid-columns-3 divide-x-4">
  <div>01</div>
  <div>02</div>
  <div>03</div>
</div>

<div class="flex flex-col divide-y-4">
  <div>01</div>
  <div>02</div>
  <div>03</div>
</div>
`
      }
    }
  ],
  htmlData: [
    {
      data: `
      <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
        Use <code class="text-color-gray-800 dark:text-color-white font-semibold">rounded-sm</code>, and <code class="text-color-gray-800 dark:text-color-white font-semibold">rounded-md</code> to apply different border radius sizes to an element:
      </p>
      <div class="flex justify-center gap-6 items-center  font-mono text-sm font-bold flex-wrap">
          <div class="border border-color-blue-500 flex justify-center items-center p-4 h-20">border</div>
          <div class="border border-color-blue-500 flex justify-center items-center p-4 h-20 border-2">border-2</div>
          <div class="border border-color-blue-500 flex justify-center items-center p-4 h-20 border-4">border-4</div>
          <div class="border border-color-blue-500 flex justify-center items-center p-4 h-20 border-[10px]">border-[10px]</div>
      </div>
      `
    },
    {
      data: `
      <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
        Use <code class="text-color-gray-800 dark:text-color-white font-semibold">border-t</code>, and <code class="text-color-gray-800 dark:text-color-white font-semibold">border-r-w-4</code>
        to set the border width for one side of an element:
      </p>
      <div class="flex justify-center gap-6 items-center  font-mono text-sm font-bold flex-wrap">
          <div class="border-t outline outline-style-dashed outline-color-gray-300 dark:outline-color-gray-500 border-color-blue-500 flex justify-center items-center p-4 h-20">border-t</div>
          <div class="border-r-2 outline outline-style-dashed outline-color-gray-300 dark:outline-color-gray-500  border-color-blue-500 flex justify-center items-center p-4 h-20">border-r-2</div>
          <div class="border-b-4 outline outline-style-dashed outline-color-gray-300 dark:outline-color-gray-500  border-color-blue-500 flex justify-center items-center p-4 h-20">border-b-4</div>
          <div class="border-l-5 outline outline-style-dashed outline-color-gray-300 dark:outline-color-gray-500  border-color-blue-500 flex justify-center items-center p-4 h-20">border-l-5</div>
      </div>
      `
    },
    {
      data: `
      <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
        Use <code class="text-color-gray-800 dark:text-color-white font-semibold">border-x</code>, and <code class="text-color-gray-800 dark:text-color-white font-semibold">border-y-4</code>
        to set the border width on two sides of an element at the same time:
      </p>
      <div class="flex justify-center gap-6 items-center  font-mono text-sm font-bold flex-wrap">
          <div class="border-x-4 outline outline-style-dashed outline-color-gray-300 dark:outline-color-gray-500  border-color-blue-500 flex justify-center items-center p-4 h-20">border-x-4</div>
          <div class="border-y-4 outline outline-style-dashed outline-color-gray-300 dark:outline-color-gray-500  border-color-blue-500 flex justify-center items-center p-4 h-20">border-y-4</div>
      </div>
      `
    },
    {
      data: `
      <p class="text-color-gray-600 text-sm dark:text-color-gray-300 mb-10">
        Use <code class="text-color-gray-800 dark:text-color-white font-semibold">divide-x</code>, and <code class="text-color-gray-800 dark:text-color-white font-semibold">divide-y-4</code>
        to add borders between child elements:
      </p>
      <div class="grid grid-columns-3 divide-x-4 divide-color-blue-500 *:flex *:justify-center *:items-center *:p-4">
        <div class="outline outline-style-dashed outline-color-gray-300 dark:outline-color-gray-500">01</div>
        <div class="outline outline-style-dashed outline-color-gray-300 dark:outline-color-gray-500">02</div>
        <div class="outline outline-style-dashed outline-color-gray-300 dark:outline-color-gray-500">03</div>
      </div>

      <div class="flex flex-col mt-10 divide-y-4 divide-color-blue-500 *:flex *:justify-center *:items-center *:p-4">
        <div class="outline outline-style-dashed outline-color-gray-300 dark:outline-color-gray-500">01</div>
        <div class="outline outline-style-dashed outline-color-gray-300 dark:outline-color-gray-500">02</div>
        <div class="outline outline-style-dashed outline-color-gray-300 dark:outline-color-gray-500">03</div>
      </div>
      `
    },
  ]
};

export default borderwidth;
<div className="rounded-sm divide-x-4  divide-y-4 divide-y divide-color-blue-500 *:flex *:justify-center *:items-center *:p-4 font-mono text-sm font-bold border-t border-x-4 border-y-4 dark:text-color-gray-300 outline-style-dashed dark:outline-color-gray-500  outline-color-gray-300 border-r-2 border-b-4 border-l-5  border-2 border-4 border-8  border-[10px] border border-color-blue-500 bg-color-emerald-600 rounded-[10px] rounded-2xl rounded-t-2xl rounded-r-2xl rounded-b-2xl rounded-l-2xl rounded-tl-2xl rounded-tr-2xl rounded-br-2xl rounded-bl-2xl"></div>